/**
 * CARGA DE TIPOGRAFIAS ANTES DE MEDIR.
 *
 * `@font-face` con `font-display: swap` pinta primero con una fuente de
 * sustitucion y cambia cuando llega la buena. En una pagina normal eso es lo
 * correcto; en este editor es un error caro: el ancho de la caja de texto y las
 * posiciones del arco se MIDEN, y si se miden con la fuente de sustitucion
 * quedan mal y ya no se vuelven a calcular. El cliente ve el texto cortado o
 * las letras del arco amontonadas.
 *
 * Por eso se espera explicitamente con `document.fonts.load` y se repinta.
 */

/** Cache de lo ya pedido: `document.fonts.load` no descarga dos veces, pero cada llamada crea promesas. */
const pedidas = new Set<string>();

function clave(familia: string, peso: number, cursiva: boolean): string {
  return `${familia}|${peso}|${cursiva ? 'i' : 'n'}`;
}

export interface FuentePedida {
  family: string;
  weight: number;
  italic: boolean;
}

/**
 * Espera a que esten disponibles todas las combinaciones pedidas.
 *
 * Nunca rechaza: si una fuente no llega —red caida, fichero que falta— vale mas
 * pintar con la de sustitucion que dejar el lienzo en blanco. El aviso de que
 * algo no cuadra ya lo dara el propio texto al verse distinto.
 */
export async function cargarFuentes(fuentes: readonly FuentePedida[]): Promise<void> {
  if (typeof document === 'undefined' || !('fonts' in document)) return;

  const nuevas = fuentes.filter((f) => !pedidas.has(clave(f.family, f.weight, f.italic)));
  if (nuevas.length === 0) return;

  await Promise.all(
    nuevas.map(async (fuente) => {
      pedidas.add(clave(fuente.family, fuente.weight, fuente.italic));
      // El tamano da igual para la descarga, pero la sintaxis lo exige.
      const descriptor = `${fuente.italic ? 'italic ' : ''}${fuente.weight} 16px "${fuente.family}"`;
      try {
        await document.fonts.load(descriptor);
      } catch {
        // Se ignora a proposito: ver la nota de arriba.
      }
    }),
  );
}

/** Si todas estas combinaciones ya estan listas para pintar. */
export function fuentesListas(fuentes: readonly FuentePedida[]): boolean {
  if (typeof document === 'undefined' || !('fonts' in document)) return true;
  return fuentes.every((f) =>
    document.fonts.check(`${f.italic ? 'italic ' : ''}${f.weight} 16px "${f.family}"`),
  );
}
